import * as React from 'react'
import CircularProgress from '@mui/material/CircularProgress'
import classNames from 'classnames'
import iassign from 'immutable-assign'
import _ from 'lodash'
import moment from 'moment'
import { VyneTable } from '@vynedental/design-system'

import { SortingDirection } from '../../models/enums'
import { moveDefaultLocationToTop } from '../../modules/shared/sortUtils'
import { fetchAdmins } from '../admin/actions'
import { fetchReferralList } from '../amplify/actions'
import CustomMultiselectField from '../shared/custom-fields/CustomMultiselectField'
import { SearchBar } from '../shared/custom-fields/SearchBar'
import InfoMessage from '../shared/info-message/info-message'
import Paginator from '../shared/Paginator'

import { convertDoubleStruckToRegular } from '../../modules/shared/unicodeCharsUtils'
import CopyText from '../shared/CopyText'

import AmplifyReferralActions from './AmplifyReferralActions'

import './AmplifyManagement.sass'

export type AmplifyManagementProps = Readonly<{
    practice: Models.Practice
    referrals?: Array<Models.Referral>
    admins: { [key: string]: Models.Account }
    paginationInfo?: Models.PaginationInfo
}>

export type AmplifyManagementDispatch = Readonly<{
    fetchReferralList: typeof fetchReferralList
    fetchAdmins: typeof fetchAdmins
}>

export type AmplifyManagementState = {
    days: number | 'all'
    searchTerms: ApiV2.Amplify.ReferralSearchTerms
}

type Props = AmplifyManagementProps & AmplifyManagementDispatch

const pickerDaysRanges = [
    { id: 'all', name: 'All Time' },
    { id: '0', name: 'Today' },
    { id: '7', name: 'Last 7 Days' },
    { id: '30', name: 'Last 30 Days' },
    { id: '90', name: 'Last 90 Days' },
]

class AmplifyManagement extends React.Component<Props, AmplifyManagementState> {
    throttledFetchReferralList: () => void

    constructor(props: Props) {
        super(props)
        this.state = {
            days: 'all',
            searchTerms: {
                page: 1,
                location_ids: undefined,
                sort: 'updated',
                order: 'desc',
            },
        }

        this.throttledFetchReferralList = _.throttle(this.fetchReferralList, 750)
    }

    UNSAFE_componentWillMount() {
        this.props.fetchReferralList(this.props.practice, {})
        this.props.fetchAdmins('amplify-chatters', { limit: 0 })
    }

    getSortOrder = (field: string) => {
        if (this.state.searchTerms.sort !== field) return null
        return this.state.searchTerms.order === 'asc' ? ('ascend' as const) : ('descend' as const)
    }

    handleTableChange = (pagination: any, filters: any, sorter: any) => {
        if (sorter && sorter.columnKey && sorter.order) {
            const order: SortingDirection = sorter.order === 'ascend' ? 'asc' : 'desc'
            this.setSearchTermsState({ sort: sorter.columnKey, order })
        } else if (sorter && !sorter.order) {
            const flipped: SortingDirection = this.state.searchTerms.order === 'desc' ? 'asc' : 'desc'
            this.setSearchTermsState({ order: flipped })
        }
    }

    selectDays = (values: string[]) => {
        const value = values[0]
        const days = value === 'all' ? value : parseInt(value, 10)
        this.setState({ days }, () =>
            this.setSearchTermsState({
                start_date:
                    value !== 'all'
                        ? moment()
                              .startOf('day')
                              .subtract(value, 'days')
                              .toISOString()
                        : undefined,
            }),
        )
    }
    selectLocation = (values: string[]) => {
        const value = values[0] ?? 'all'
        const locationId = value !== 'all' ? value : undefined
        this.setSearchTermsState({ location_ids: locationId })
    }
    selectPage = (page: number) => {
        this.setSearchTermsState({ page })
    }
    searchByKeyword = (e: React.ChangeEvent<HTMLInputElement>) => {
        const searchKey = e.target && e.target.value
        this.setSearchTermsState({ search: searchKey, page: 1 })
    }
    clearSearch = () => {
        const searchKey = ''
        this.setSearchTermsState({ search: searchKey, page: 1 })
    }
    setSearchTermsState = (searchTerms: ApiV2.Amplify.ReferralSearchTerms) => {
        this.setState(
            iassign(
                this.state,
                next => next.searchTerms,
                next => {
                    return {
                        ...next,
                        ...searchTerms,
                    }
                },
            ),
            () => this.throttledFetchReferralList(),
        )
    }
    fetchReferralList = () => {
        this.props.fetchReferralList(this.props.practice, { ...this.state.searchTerms })
    }

    render() {
        const { practice, referrals, admins, paginationInfo } = this.props
        const {
            days,
            searchTerms: { location_ids = 'all', page = 1, search = '' },
        } = this.state

        const sortedLocations = _.sortBy([...practice.locations], l => l.name.toLowerCase()).sort(
            moveDefaultLocationToTop,
        )

        const storedLocationsWithAll = [{ id: 'all', name: 'All Locations' }, ...sortedLocations]

        if (!referrals) {
            return (
                <div className="circular-progress-loader">
                    <CircularProgress size={70} color="primary" variant="indeterminate" />
                </div>
            )
        }

        const columns = [
            {
                title: 'ID',
                key: 'id',
                width: '6%',
                render: (_: any, referral: any) => (
                    <span className="id">
                        <CopyText text={referral.id}>ID</CopyText>
                    </span>
                ),
            },
            {
                title: 'Last Name',
                key: 'amplify_referral_patient.last_name',
                width: '8%',
                sorter: true,
                sortOrder: this.getSortOrder('amplify_referral_patient.last_name'),
                render: (_: any, referral: any) => {
                    const patient = referral.amplify_referral_patients?.find((p: any) => p.is_primary) || referral.amplify_referral_patients?.[0]
                    return (
                        <div className="patient-name">
                            {convertDoubleStruckToRegular(patient?.last_name || '')}
                            {referral.isTest && <span className="test-referral"> [TEST]</span>}
                        </div>
                    )
                },
            },
            {
                title: 'First Name',
                key: 'amplify_referral_patient.first_name',
                width: '9%',
                sorter: true,
                sortOrder: this.getSortOrder('amplify_referral_patient.first_name'),
                render: (_: any, referral: any) => {
                    const patient = referral.amplify_referral_patients?.find((p: any) => p.is_primary) || referral.amplify_referral_patients?.[0]
                    return (
                        <div className="patient-name">
                            {convertDoubleStruckToRegular(patient?.first_name || '')}
                            {referral.isTest && <span className="test-referral"> [TEST]</span>}
                        </div>
                    )
                },
            },
            {
                title: 'Created',
                dataIndex: 'created',
                key: 'created',
                width: '14%',
                sorter: true,
                sortOrder: this.getSortOrder('created'),
                render: (created: any, referral: any) => (
                    <div className="last-edit">
                        {moment(created).format('MM-DD-YYYY hh:mm a')}
                        <div className="time-since">{moment().to(referral.created)}</div>
                    </div>
                ),
            },
            {
                title: 'Last Edit',
                dataIndex: 'updated',
                key: 'updated',
                width: '14%',
                sorter: true,
                sortOrder: this.getSortOrder('updated'),
                render: (updated: any) => (
                    <div className="last-edit">{moment(updated).format('MM-DD-YYYY hh:mm a')}</div>
                ),
            },
            {
                title: 'Request Writer',
                key: 'referral_creator.last_name',
                width: '16%',
                sorter: true,
                sortOrder: this.getSortOrder('referral_creator.last_name'),
                render: (_: any, referral: any) => {
                    const creator = admins[referral.referralCreatorId]
                    const writerName = creator
                        ? creator.name
                        : referral.referral_writer
                        ? `${referral.referral_writer.first_name} ${referral.referral_writer.last_name}`
                        : referral.referralCreatorId
                    return <span>{writerName}</span>
                },
            },
            {
                title: 'Chatter',
                key: 'inbound_handler.last_name',
                width: '16%',
                sorter: true,
                sortOrder: this.getSortOrder('inbound_handler.last_name'),
                render: (_: any, referral: any) => {
                    const handler = admins[referral.inboundHandlerId]
                    const chatterName = handler
                        ? handler.name
                        : referral.chatter
                        ? `${referral.chatter.first_name} ${referral.chatter.last_name}`
                        : referral.inboundHandler
                        ? `${referral.inboundHandler.first_name} ${referral.inboundHandler.last_name}`
                        : referral.inboundHandlerId
                    return <span>{chatterName}</span>
                },
            },
            {
                title: 'Status',
                key: 'amplify_status.value',
                width: '10%',
                sorter: true,
                sortOrder: this.getSortOrder('amplify_status.value'),
                render: (_: any, referral: any) => {
                    if (!referral.status) return null
                    const formatted = referral.status.value.charAt(0).toUpperCase() + referral.status.value.slice(1)
                    return formatted.replace('_', ' ')
                },
            },
            {
                title: '',
                key: 'actions',
                width: '7%',
                render: (_: any, referral: any) => (
                    <AmplifyReferralActions
                        referralId={referral.id}
                        v2referral={referral}
                        practice={practice}
                        referralCreator={admins[referral.referralCreatorId]}
                        inboundHandler={admins[referral.inboundHandlerId]}
                    />
                ),
            },
        ]

        return (
            <div className={classNames('amplify-management')}>
                <div className="filter-select">
                    <div className="amplify-management-select-location">
                        <CustomMultiselectField
                            items={storedLocationsWithAll}
                            maxSelected={1}
                            selectedItems={[location_ids]}
                            keyProperty="id"
                            displayProperty="name"
                            placeholder="Select location"
                            onSelectElement={this.selectLocation}
                            search={sortedLocations?.length > 8}
                            searchPlaceholder="Search Location"
                        />
                    </div>
                    <div className="amplify-management-select-days">
                        <CustomMultiselectField
                            items={pickerDaysRanges}
                            maxSelected={1}
                            selectedItems={[days.toString()]}
                            keyProperty="id"
                            displayProperty="name"
                            placeholder="Select time"
                            onSelectElement={this.selectDays}
                        />
                    </div>
                    <div className="amplify-management-search-box">
                        <SearchBar
                            value={search}
                            onChange={this.searchByKeyword}
                            onClear={this.clearSearch}
                            placeholder="Search by Request Name or Request ID"
                        />
                    </div>
                </div>
                <div className="amplify-management__production-values">
                    <div className="amplify-management__production-value">
                        <div className="amplify-management__production-value-label">Requests</div>
                        <div className="amplify-management__production-value-sum">{paginationInfo?.allRows ?? 0}</div>
                    </div>
                </div>
                {referrals.length ? (
                    <VyneTable
                        className="amplify-management-table"
                        columns={columns}
                        dataSource={referrals}
                        rowKey="id"
                        pagination={false}
                        tableLayout="fixed"
                        onChange={this.handleTableChange}
                        dataTestId="amplify-management-table"
                    />
                ) : (
                    <InfoMessage isShown={true}>No records found for selected criteria</InfoMessage>
                )}
                <div className="paginator-wrapper">
                    <Paginator currentPage={page} paginationInfo={paginationInfo} selectPage={this.selectPage} />
                </div>
            </div>
        )
    }
}

export default AmplifyManagement
